/**
 * RecurringTopUpSelector Component
 *
 * Lets the user set up, change, or cancel a monthly recurring wallet top-up.
 * Amount options: $10, $25, $50, $100, or a custom amount ($5-$1,000).
 * No plan names, no feature comparisons, no canvas limits.
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { PaymentFailureBanner } from './PaymentFailureBanner'
import { useSubscription } from '@/hooks/useSubscription'

const PRESET_AMOUNTS = [10, 25, 50, 100]

export function RecurringTopUpSelector() {
  const {
    subscription,
    error,
    refetch,
    isPastDue,
    isUnpaid,
    isLoading,
    isCreating,
    isUpdating,
    isCanceling,
    isResuming,
    createRecurringTopUp,
    updateAmount,
    cancelRecurringTopUp,
    resumeRecurringTopUp,
    openBillingPortal,
  } = useSubscription()

  const [selectedAmount, setSelectedAmount] = useState<number | null>(null)
  const [customAmount, setCustomAmount] = useState('')
  const [isCustom, setIsCustom] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)

  const actionLoading = isLoading || isCreating || isUpdating || isCanceling || isResuming

  // Current recurring amount in dollars (subscription stores cents)
  const currentAmountDollars = subscription ? subscription.recurringAmount / 100 : 0
  const hasRecurring = !!subscription && subscription.status !== 'CANCELED'
  const isCancelingAtEnd = hasRecurring && (subscription?.cancelAtPeriodEnd ?? false)

  // Determine the effective selected amount
  const effectiveAmount = isCustom ? parseFloat(customAmount) || 0 : selectedAmount

  const isValidAmount = effectiveAmount !== null && effectiveAmount >= 5 && effectiveAmount <= 1000
  const isSameAsCurrent = hasRecurring && effectiveAmount === currentAmountDollars

  if (isLoading && !subscription) {
    return (
      <div className="flex flex-col gap-3">
        <h4 className="text-sm font-medium text-foreground">Recurring Top-Up</h4>
        <div className="bg-muted/50 h-[140px] animate-pulse rounded-lg border border-border" />
      </div>
    )
  }

  const handleCreate = async () => {
    if (!effectiveAmount || !isValidAmount) return
    try {
      await createRecurringTopUp(effectiveAmount)
    } catch {
      // Error surfaced by the hook
    }
  }

  const handleUpdate = async () => {
    if (!effectiveAmount || !isValidAmount || isSameAsCurrent) return
    try {
      await updateAmount(effectiveAmount)
      setSelectedAmount(null)
      setIsCustom(false)
      setCustomAmount('')
    } catch {
      // Error surfaced by the hook
    }
  }

  const handleCancel = async () => {
    if (!confirmCancel) {
      setConfirmCancel(true)
      return
    }
    try {
      await cancelRecurringTopUp()
      setConfirmCancel(false)
    } catch {
      // Error surfaced by the hook
    }
  }

  const handleResume = async () => {
    try {
      await resumeRecurringTopUp()
    } catch {
      // Error surfaced by the hook
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-foreground">Recurring Top-Up</h4>
        {hasRecurring && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void openBillingPortal()}
            disabled={actionLoading}
            className="text-xs"
          >
            <Icon name="external-link" className="mr-1 h-3 w-3" />
            Manage billing
          </Button>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="border-destructive/30 flex items-center justify-between gap-3 rounded-lg border p-3"
        >
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={refetch}>
            Retry recurring top-up
          </Button>
        </div>
      )}
      {(isPastDue || isUnpaid) && (
        <PaymentFailureBanner
          status={isUnpaid ? 'UNPAID' : 'PAST_DUE'}
          onUpdatePayment={() => void openBillingPortal()}
        />
      )}

      {/* Active recurring info */}
      {hasRecurring && !isCancelingAtEnd && subscription?.status === 'ACTIVE' && (
        <div className="bg-primary/5 border-primary/20 flex items-center gap-2 rounded-lg border p-3">
          <Icon name="repeat" className="h-4 w-4 shrink-0 text-primary" />
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">
              ${currentAmountDollars.toFixed(2)}/month active
            </p>
            <p className="text-xs text-muted-foreground">
              Automatically adds ${currentAmountDollars.toFixed(2)} to your balance every month
              {subscription?.currentPeriodEnd && (
                <> — next charge {new Date(subscription.currentPeriodEnd).toLocaleDateString()}</>
              )}
            </p>
          </div>
        </div>
      )}

      {subscription?.status === 'INCOMPLETE' && (
        <p className="text-sm text-muted-foreground">
          Your recurring top-up is awaiting payment. Open Manage billing to review it.
        </p>
      )}

      {/* Canceling notice */}
      {isCancelingAtEnd && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
          <Icon name="alert-triangle" className="h-4 w-4 shrink-0 text-amber-500" />
          <div className="flex-1">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Your recurring top-up of ${currentAmountDollars.toFixed(2)}/mo will end
              {subscription?.currentPeriodEnd && (
                <> on {new Date(subscription.currentPeriodEnd).toLocaleDateString()}</>
              )}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleResume()}
            disabled={actionLoading}
            className="ml-auto shrink-0"
          >
            {isResuming ? 'Resuming...' : 'Resume'}
          </Button>
        </div>
      )}

      {/* Amount selector */}
      {!isCancelingAtEnd && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-muted-foreground">
            {hasRecurring ? 'Change your monthly amount' : 'Choose a monthly top-up amount'}
          </p>

          <div className="flex flex-wrap gap-2">
            {PRESET_AMOUNTS.map((amount) => {
              const isActive = !isCustom && selectedAmount === amount
              const isCurrent =
                hasRecurring && amount === currentAmountDollars && !selectedAmount && !isCustom

              return (
                <button
                  key={amount}
                  type="button"
                  onClick={() => {
                    setSelectedAmount(amount)
                    setIsCustom(false)
                    setCustomAmount('')
                    setConfirmCancel(false)
                  }}
                  disabled={actionLoading}
                  className={cn(
                    'rounded-lg border px-4 py-2 text-sm font-medium transition-all',
                    isActive
                      ? 'bg-primary/10 border-primary text-primary'
                      : isCurrent
                        ? 'border-primary/40 bg-primary/5 text-foreground'
                        : 'hover:border-primary/30 border-border bg-background text-foreground'
                  )}
                >
                  ${amount}/mo
                  {isCurrent && !isActive && (
                    <span className="ml-1 text-xs text-muted-foreground">(current)</span>
                  )}
                </button>
              )
            })}

            {/* Custom amount toggle */}
            <button
              type="button"
              onClick={() => {
                setIsCustom(true)
                setSelectedAmount(null)
                setConfirmCancel(false)
              }}
              disabled={actionLoading}
              className={cn(
                'rounded-lg border px-4 py-2 text-sm font-medium transition-all',
                isCustom
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'hover:border-primary/30 border-border bg-background text-foreground'
              )}
            >
              Custom
            </button>
          </div>

          {/* Custom amount input */}
          {isCustom && (
            <div className="mt-1 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">$</span>
              <Input
                aria-label="Monthly top-up amount"
                type="number"
                min={5}
                max={1000}
                step={1}
                placeholder="Enter amount (5-1,000)"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className="w-48"
                disabled={actionLoading}
              />
              <span className="text-sm text-muted-foreground">/month</span>
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      {!isCancelingAtEnd && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {!hasRecurring ? (
            <Button
              variant="default"
              size="sm"
              onClick={() => void handleCreate()}
              disabled={actionLoading || !!error || !isValidAmount}
            >
              {isCreating ? 'Redirecting...' : 'Start recurring top-up'}
            </Button>
          ) : (
            <>
              {(selectedAmount || isCustom) && !isSameAsCurrent && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => void handleUpdate()}
                  disabled={actionLoading || !!error || !isValidAmount || isSameAsCurrent}
                >
                  {isUpdating ? 'Updating...' : `Change to $${effectiveAmount}/mo`}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleCancel()}
                disabled={actionLoading}
                className="text-muted-foreground"
              >
                {confirmCancel
                  ? isCanceling
                    ? 'Canceling...'
                    : 'Confirm cancellation'
                  : 'Cancel recurring top-up'}
              </Button>
            </>
          )}
        </div>
      )}

      {(selectedAmount || isCustom) && !isSameAsCurrent && hasRecurring && !isCancelingAtEnd && (
        <p className="text-[11px] text-muted-foreground">
          New amount takes effect on your next billing cycle
        </p>
      )}
    </div>
  )
}
